import { Binding } from 'core/infrastructure';

import { ColumnFilter, matchesFilter } from './DataGrid.Filter';
import { DataGridColumn, applyColumnFormat } from './DataGrid.Column';
import { SortDirection } from './DataGrid.SortHeaderCell';

export type FilterCriteria<T> = (item: T) => boolean;

function toCriteriaFunc<T>(searchTextOrCriteria: string | FilterCriteria<T>, columns: DataGridColumn<T>[]) {
	// El primer filtro es en base al searchTextOrCriteria
	if (typeof searchTextOrCriteria === 'string') {
		const searchText = searchTextOrCriteria && searchTextOrCriteria.toLowerCase();

		// Optimizo el caso de no tener que filtrar por texto
		if (searchText === '')
			return () => true;

		// Evito recalcular el propertyPathToString para cada fila.
		const columnToPropertyPath = new Map<DataGridColumn<T>, string>();

		for (const column of columns)
			columnToPropertyPath.set(column, Binding.propertyPathToString(column.linkTo));

		const isMatch = (column: DataGridColumn<T>, item: T) => {
			const value = Binding.getValue(item, columnToPropertyPath.get(column)!);
			const formattedValue = applyColumnFormat(column, value, item);
			return formattedValue.toLowerCase().indexOf(searchText) >= 0;
		};

		return (item: T) => columns.some(col => isMatch(col, item));
	}

	return searchTextOrCriteria;
}


// Memoizo la última llamada a filterAndSort para evitar que se invoque más veces en los re-renders
const filterAndSortCache: {
	params?: {
		items: any[],
		columns: DataGridColumn<any>[],
		searchTextOrCriteria: string | FilterCriteria<any>,
		columnFilters: ColumnFilter<any>[],
		sortBy: Nullable<string>,
		direction: Nullable<SortDirection>
	},
	result?: any[]
} = {};

export function filterAndSort<T>(
	items: T[], columns: DataGridColumn<T>[],
	searchTextOrCriteria: string | FilterCriteria<T>, columnFilters: ColumnFilter<T>[],
	sortBy: Nullable<string>, direction: Nullable<SortDirection>): T[] {

	if (items === filterAndSortCache.params?.items && columns === filterAndSortCache.params?.columns
		&& searchTextOrCriteria === filterAndSortCache.params?.searchTextOrCriteria
		&& columnFilters === filterAndSortCache.params?.columnFilters
		&& sortBy === filterAndSortCache.params?.sortBy
		&& direction === filterAndSortCache.params?.direction
	)
		return filterAndSortCache.result!;

	filterAndSortCache.params = {
		items, columns, searchTextOrCriteria, columnFilters, sortBy, direction
	};

	const searchFunc = toCriteriaFunc(searchTextOrCriteria, columns);

	const filterCriteria = columnFilters.length > 0
		? (item: T) => searchFunc(item) && matchesFilter(item, columnFilters)
		: searchFunc;

	// Siempre hago el filter para aprovechar para copiar
	const data = items.filter(x => filterCriteria(x));

	// Ahora puedo ordenar "in place" porque data es una copia de los items originales
	if (sortBy) {

		const sortCriteria = ((item: T) => Binding.getValue(item, sortBy));
		const multiplier = direction === 'desc' ? -1 : 1;

		data.sort((a: any, b: any) => {
			const aVal = sortCriteria(a) ?? '';
			const bVal = sortCriteria(b) ?? '';

			let compare = 0;

			if (typeof aVal === 'string' && typeof bVal === 'string') {
				compare = aVal.localeCompare(bVal);
			} else {
				if (aVal < bVal)
					compare = -1;
				else if (aVal === bVal)
					compare = 0;
				else
					compare = 1;
			}

			return multiplier * compare;
		});
	}

	filterAndSortCache.result = data;

	return data;
}
